import React, { useEffect, useState } from 'react';
import { components, DropdownIndicatorProps } from 'react-select';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import classNames from 'classnames';
import { Icon } from '@/components/shared';
import styles from './ReactSelect.module.scss';

export interface ReactSelectOptionProps {
	value: string;
	label: string;
}

interface ReactSelectProps {
	label?: React.ReactNode;
	ariaLabel: string;
	onChange: (e) => void;
	id: string;
	isSearchable?: boolean;
	name?: string;
	placeholder?: string;
	options?: ReactSelectOptionProps[];
	currentValue?: ReactSelectOptionProps | null;
	required?: boolean;
	errorText?: string;
	variant?: 'default' | 'inverse';
	onInputChange?: (e: string) => void;
	creatable?: boolean;
	handleCreate?: (value?) => void;
	hideLabel?: boolean;
	wrapperClassName?: string;
	isSmall?: boolean;
}

/**
 * Generic React Select readonly component, build with react-select package.
 * choose between existing options
 * NB: this package is huge - if it can be replaced with custom code or small package it will be preferred
 */

export const ReactSelect: React.FunctionComponent<ReactSelectProps> = ({
	label,
	ariaLabel,
	name,
	id,
	placeholder,
	options,
	currentValue,
	required,
	errorText,
	variant,
	onChange,
	isSearchable,
	onInputChange,
	creatable,
	handleCreate,
	hideLabel,
	wrapperClassName,
	isSmall,
}) => {
	const creatableStyles = {
		control: (styles) => ({
			...styles,
			height: isSmall ? '40px' : '48px',
			borderRadius: '0',
			backgroundColor: variant === 'inverse' ? '#f2f0ed' : '#ffffff',
			borderColor: errorText ? '#b72941' : 'transparent',
			borderBottomColor: errorText ? '#b72941' : '#cfcecd',
			paddingLeft: '8px',
			boxShadow: 'none',
			':hover': { borderColor: '#6f6e6e' },
		}),
		indicatorSeparator: (styles) => ({ ...styles, display: 'none' }),
		dropdownIndicator: (styles) => ({ ...styles, color: '#000' }),
		placeholder: (styles) => ({ ...styles, color: '#3f3e3e' }),
	};
	const [defaultValue, setDefaultValue] = useState(currentValue);

	const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
		return (
			<components.DropdownIndicator {...props}>
				<Icon name={'arrowLeft'} rotate={'270'} size={'md'} />
			</components.DropdownIndicator>
		);
	};

	useEffect(() => {
		setDefaultValue(currentValue);
	}, [currentValue]);

	return (
		<div className={wrapperClassName}>
			<label
				htmlFor={id}
				className={classNames(styles.label, {
					[styles.required]: required,
					[styles.hasError]: !!errorText?.length,
					['u-hide-visually']: hideLabel,
				})}
			>
				{label || ariaLabel}
			</label>
			{creatable ? (
				<CreatableSelect
					value={defaultValue}
					placeholder={placeholder}
					onChange={(e) => onChange(e)}
					onCreateOption={handleCreate}
					options={options}
					styles={creatableStyles}
					components={{ DropdownIndicator }}
					aria-label={ariaLabel}
					id={id}
				/>
			) : (
				<Select
					value={defaultValue}
					placeholder={placeholder}
					onChange={onChange}
					options={options}
					styles={creatableStyles}
					components={{ DropdownIndicator }}
					aria-label={ariaLabel}
					id={id}
					name={name}
					isSearchable={isSearchable}
					onInputChange={onInputChange}
				/>
			)}
			{errorText && <p className={styles.errorText}>{errorText}</p>}
		</div>
	);
};
